import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosConstants from "./axiosConstants";

const settings = {
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
    "X-XSRF-TOKEN": axiosConstants.XSRF,
    Accept: "application/json"
  }
};

interface AxiosInstanceType {
  [x: string]: any;
  get: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
  post: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  put: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  patch: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  delete: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
}

const getAxiosInstance = (
  _instance: AxiosInstance,
  getAdditionalHeaders?: () => Record<string, string>
) => {
  return {
    async get(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.get(url, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async post(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.post(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async put(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.put(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async patch(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.patch(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async delete(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.delete(url, {
          ...config,
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    }
  };
};

const baseURLBatchCredit =
  process.env.NODE_ENV === "production"
    ? "batch-credit-ui-portlet"
    : "https://batch-credit-processor.k8.isw.la/batch-credit-processor";

const instanceBatchCredit = axios.create({
  ...settings,
  baseURL: baseURLBatchCredit
});

if (process.env.NODE_ENV !== "production") {
  instanceBatchCredit.defaults.headers.common.Authorization =
    "Bearer eyJhbGciOiJSUzI1NiJ9.eyJsYXN0TmFtZSI6ImlzdyIsIm1lcmNoYW50X2NvZGUiOiJNWDE1Nzg1IiwiZmlyc3RMb2dpbiI6ZmFsc2UsInVzZXJfbmFtZSI6ImFnYmlzd0BzaGFya2xhc2Vycy5jb20iLCJyZXF1ZXN0b3JfaWQiOiIxMjM5ODQ4NDc3MyIsImRvbWFpbl9jb2RlIjoiSVNXIiwibW9iaWxlTm8iOm51bGwsImNsaWVudF90eXBlIjoiREVGQVVMVCIsImFwcGxpY2F0aW9uX2lkIjoiNSIsInBheWFibGVfaWQiOiIzNjA0NCIsImNsaWVudF9pZCI6Imlzdy1wb3J0YWwiLCJpbnN0aXR1dGlvbl9pZCI6IjIwIiwiZmlyc3ROYW1lIjoiYWdlbmN5X2JhbmtpbmciLCJlbWFpbFZlcmlmaWVkIjp0cnVlLCJhdWQiOlsiYXJiaXRlciIsImNhZXNhciIsImNhcmRsZXNzLXNlcnZpY2UiLCJoaW1zLXBvcnRsZXQiLCJpbmNvZ25pdG8iLCJpc3ctY29sbGVjdGlvbnMiLCJpc3ctY29yZSIsImlzdy1sZW5kaW5nLXNlcnZpY2UiLCJpc3ctcGF5bWVudGdhdGV3YXkiLCJpc3ctcG9zdC1vZmZpY2UiLCJuaWJzcy1pZHJpcy1jb3B5LXNldHRpbmdzIiwicGFzc3BvcnQiLCJwb3N0Y2FyZC1hcGkiLCJwcm9qZWN0LXgtbWVyY2hhbnQiLCJyZWN1cnJlbnQtYmlsbGluZy1hcGkiLCJyZWZlcnJhbC1zZXJ2aWNlLWFwaSIsInNhZmV0b2tlbi1hZG1pbi1hcGkiLCJ0cmFuc2Zlci1zZXJ2aWNlLWFkbWluIiwidHJhbnNmZXItc2VydmljZS1jb3JlIiwidHMtbmFtZS1lbnF1aXJ5IiwidmF1bHQiLCJ3YWxsZXQiLCJ3ZWJwYXktcG9ydGxldCJdLCJjb3JlX2lkIjoiMTAwIiwic2NvcGUiOlsicHJvZmlsZSJdLCJleHAiOjE3MDY1NjQ5OTcsIm1vYmlsZU5vVmVyaWZpZWQiOmZhbHNlLCJjbGllbnRfbmFtZSI6IklTVyBCdXNpbmVzcyBQb3J0YWwiLCJqdGkiOiJkMDI0OTExMS01Y2FmLTQ1Y2EtOThjMS1hZWQxNDg1MDc4ODUiLCJlbWFpbCI6ImFnYmlzd0BzaGFya2xhc2Vycy5jb20iLCJwYXNzcG9ydElkIjoiRUIxQzEyQUItRTg0MC00QzRDLTgyODEtN0E3NzYwNkJBRTAyIn0.PiX8b0HmCUMal1TijLbFtfXNCrF2jajcPS_q-ko-UcrZUYFCb-kl5yDwZZsfDmKgm3G5ylDspssgTZuM9sYXuqg9NHdY_R0iS_-5zbZ3-P96uOXxYC9WtE3x_FUokQSIAwEr0nL2Slkz_0OP68DpBtHKKxdkpGL8vu181gQQ7b6MlP-jZxFmgJyXHfUzqT-jCtldtRGtYdw70q5pD_XtQezNEjPmb-wInbhipUS1EGxa8PkigbDSkuORfkLJX1VET2rRHxW6bX7bLyKzauNZVxbE9TwId-xQyr11RxuIdel_AT3ZA0FX9jrpXTUFOOgQRehfzZjjrPrMPK7BzGf1yQ";
}

export const axiosInstanceBatchCredit: AxiosInstanceType =
  getAxiosInstance(instanceBatchCredit);
